import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
    CustomInput,
    Form,
    FormGroup,
    Input,
} from 'reactstrap';

import Widget from '../../../components/Widget/Widget';
import {
    calculateStripeTaxRequest,
    loadPayrollEngineRequest,
    loadPayrollSettingsRequest,
    loadStripeTaxSettingsRequest,
    loadStripeTaxFilingRequest,
    recordStripeTaxRemittanceRequest,
    savePayrollEngineRequest,
    savePayrollSettingsRequest,
    saveStripeTaxSettingsRequest,
} from '../../../actions/settings';
import config from '../../../config';

import { withTranslation } from '../../../i18n/LanguageContext';
import s from './PayrollSettings.module.scss';

const EMPTY_SETTINGS = {
    payrollEnabled: false,
    includeTipsAndGratuityInPayroll: false,
    payrollEngineConnected: false,
    payrollEngineStatusMessage: '',
};

const EMPTY_TAX = {
    stripeTaxEnabled: false,
    offlineTaxMode: 'TaxApi',
    reserveTaxSource: 'StripeTaxNet',
    defaultTaxCode: 'txcd_40060003',
    foodTaxCode: 'txcd_40060003',
    alcoholTaxCode: 'txcd_40040000',
    merchandiseTaxCode: 'txcd_99999999',
    businessCountry: 'US',
    businessState: 'GA',
    businessCity: 'Atlanta',
    businessPostalCode: '30303',
    businessLine1: '1 Peachtree St',
};

class PayrollSettings extends React.Component {
    static propTypes = {
        dispatch: PropTypes.func.isRequired,
    };

    state = {
        settings: EMPTY_SETTINGS,
        draft: EMPTY_SETTINGS,
        taxSettings: EMPTY_TAX,
        taxDraft: EMPTY_TAX,
        taxStatus: null,
        loading: true,
        saving: false,
        taxSaving: false,
        calcBusy: false,
        calcAmount: '100.00',
        calcResult: '',
        remitBusy: false,
        remitAmount: '',
        remitResult: '',
        taxFiling: null,
        loadError: '',
        taxLoadError: '',
        engine: null,
        engineDraft: { baseUrl: '', apiKey: '' },
        engineSaving: false,
        engineLoadError: '',
        jurisdictionPackages: [],
        allowedStates: ['GA'],
        jurisdictionSaving: false,
        jurisdictionError: '',
        check: null,
        checkError: '',
    };

    componentDidMount() {
        this.loadSettings();
        this.loadTaxSettings();
        this.loadEngineSettings();
        this.loadJurisdictions();
        this.loadCheckStatus();
    }

    loadSettings = async () => {
        this.setState({ loading: true, loadError: '' });
        try {
            const settings = await this.props.dispatch(loadPayrollSettingsRequest());
            this.setState({
                settings,
                draft: settings,
                loading: false,
            });
        } catch (err) {
            this.setState({
                loading: false,
                loadError: err?.message || 'Failed to load payroll settings.',
            });
        }
    };

    loadTaxSettings = async () => {
        this.setState({ taxLoadError: '' });
        try {
            const { settings, taxStatus } = await this.props.dispatch(loadStripeTaxSettingsRequest());
            let taxFiling = this.state.taxFiling;
            try {
                taxFiling = await this.props.dispatch(loadStripeTaxFilingRequest());
            } catch {
                taxFiling = taxFiling;
            }
            this.setState({
                taxSettings: settings,
                taxDraft: settings,
                taxStatus,
                taxFiling,
            });
        } catch (err) {
            this.setState({
                taxLoadError: err?.message || 'Failed to load Stripe Tax settings.',
            });
        }
    };

    loadEngineSettings = async () => {
        this.setState({ engineLoadError: '' });
        try {
            const engine = await this.props.dispatch(loadPayrollEngineRequest());
            this.setState({
                engine,
                engineDraft: { baseUrl: engine.baseUrl || '', apiKey: '' },
            });
        } catch (err) {
            this.setState({
                engineLoadError: err?.message || 'Failed to load Payroll Engine settings.',
            });
        }
    };

    loadJurisdictions = async () => {
        this.setState({ jurisdictionError: '' });
        try {
            const res = await axios.get('/api/payroll/jurisdictions');
            if (res.data.status !== 'Ok') {
                this.setState({ jurisdictionError: res.data.message || 'Unable to load payroll states.' });
                return;
            }
            this.setState({
                jurisdictionPackages: res.data.packages || res.data.Packages || [],
                allowedStates: res.data.allowedStates || res.data.AllowedStates || ['GA'],
            });
        } catch (err) {
            this.setState({
                jurisdictionError: (err.response && err.response.data && err.response.data.message) || err.message || '',
            });
        }
    };

    loadCheckStatus = async () => {
        this.setState({ checkError: '' });
        try {
            const res = await axios.get('/api/integrations/check/status');
            if (res.data.status !== 'Ok') {
                this.setState({ checkError: res.data.message || 'Unable to load Check status.' });
                return;
            }
            this.setState({ check: res.data.check || res.data.Check || null });
        } catch (err) {
            this.setState({
                checkError: (err.response && err.response.data && err.response.data.message) || err.message || '',
            });
        }
    };

    renderCheckStatus() {
        const { check, checkError } = this.state;
        const connected = !!(check && (check.Connected || check.connected));
        const enabled = !!(check && (check.PayrollEnabled || check.payrollEnabled));
        const onboard = (check && (check.OnboardStatus || check.onboardStatus)) || '';
        const configured = !!(check && (check.AppConfigured || check.appConfigured));
        const sandbox = !!(check && (check.IsSandbox || check.isSandbox));
        return (
            <div className={s.statusBox}>
                <span className={s.detailLabel}>{this.props.t('payroll.checkPayroll')}</span>
                {checkError ? <p className={s.errorText}>{checkError}</p> : null}
                {!checkError && !check ? (
                    <p className={s.helpText}>{this.props.t('payroll.loadingCheck')}</p>
                ) : null}
                {!checkError && check && !configured ? (
                    <p className={s.helpText}>
                        Check is not configured on this server. Rebelity must set the platform API key.
                    </p>
                ) : null}
                {!checkError && check && configured && !connected ? (
                    <p className={s.helpText}>
                        Check is not connected for this location. Connect it on{' '}
                        <Link to="/admin/settings/integrations">Integrations</Link>
                        {' '}before wage ACH.
                    </p>
                ) : null}
                {!checkError && connected ? (
                    <p className={s.helpText}>
                        {enabled ? 'Check payroll is on.' : 'Check payroll is paused (worksheets stay available).'}
                        {sandbox ? ' Sandbox.' : ''}
                        {onboard ? ` Onboard: ${onboard}.` : ''}
                        {' '}
                        <Link to="/admin/settings/integrations">{this.props.t('payroll.manageIntegrations')}</Link>
                        . Wage ACH is approved in Rebelity Payroll after a Check preview — this page does not commit payruns.
                    </p>
                ) : null}
            </div>
        );
    }

    toggleJurisdiction = async (stateCode, enabled) => {
        const current = this.state.allowedStates || [];
        let next;
        if (enabled) {
            next = current.indexOf(stateCode) >= 0 ? current : current.concat([stateCode]);
        } else {
            next = current.filter((code) => code !== stateCode);
        }
        this.setState({ jurisdictionSaving: true, jurisdictionError: '' });
        try {
            const res = await axios.put('/api/payroll/tenants/current', { AllowedEmploymentStates: next });
            if (res.data.status !== 'Ok') {
                this.setState({
                    jurisdictionSaving: false,
                    jurisdictionError: res.data.message || 'Unable to update payroll states.',
                });
                return;
            }
            const tenant = res.data.tenant || {};
            this.setState({
                jurisdictionSaving: false,
                allowedStates: tenant.AllowedEmploymentStates || tenant.allowedEmploymentStates || next,
            });
            this.loadJurisdictions();
        } catch (err) {
            this.setState({
                jurisdictionSaving: false,
                jurisdictionError: err.message || 'Unable to update payroll states.',
            });
        }
    };

    handleEngineField = (field) => (event) => {
        const value = event?.target?.value;
        this.setState((prev) => ({
            engineDraft: {
                ...prev.engineDraft,
                [field]: value,
            },
        }));
    };

    handleEngineSave = async (event) => {
        if (event && event.preventDefault) {
            event.preventDefault();
        }
        const { engineDraft } = this.state;
        this.setState({ engineSaving: true, engineLoadError: '' });
        try {
            const payload = {
                BaseUrl: (engineDraft.baseUrl || '').trim(),
            };
            if ((engineDraft.apiKey || '').trim()) {
                payload.ApiKey = engineDraft.apiKey.trim();
            }
            const engine = await this.props.dispatch(savePayrollEngineRequest(payload));
            this.setState({
                engine,
                engineDraft: { baseUrl: engine.baseUrl || '', apiKey: '' },
                engineSaving: false,
                draft: {
                    ...this.state.draft,
                    payrollEngineConnected: !!engine.connected,
                    payrollEngineStatusMessage: engine.message || '',
                },
                settings: {
                    ...this.state.settings,
                    payrollEngineConnected: !!engine.connected,
                    payrollEngineStatusMessage: engine.message || '',
                },
            });
        } catch (err) {
            this.setState({
                engineSaving: false,
                engineLoadError: err?.message || 'Failed to save Payroll Engine settings.',
            });
        }
    };

    handleToggle = (field) => (event) => {
        const checked = !!(event?.target?.checked);
        this.setState((prev) => ({
            draft: {
                ...prev.draft,
                [field]: checked,
            },
        }));
    };

    handleTaxToggle = (field) => (event) => {
        const checked = !!(event?.target?.checked);
        this.setState((prev) => ({
            taxDraft: {
                ...prev.taxDraft,
                [field]: checked,
            },
        }));
    };

    handleTaxField = (field) => (event) => {
        const value = event?.target?.value;
        this.setState((prev) => ({
            taxDraft: {
                ...prev.taxDraft,
                [field]: value,
            },
        }));
    };

    handleSave = async (event) => {
        event.preventDefault();
        const { draft } = this.state;
        this.setState({ saving: true });
        try {
            const settings = await this.props.dispatch(savePayrollSettingsRequest({
                PayrollEnabled: !!draft.payrollEnabled,
                IncludeTipsAndGratuityInPayroll: !!draft.includeTipsAndGratuityInPayroll,
            }));
            this.setState({
                settings,
                draft: settings,
                saving: false,
            });
        } catch (err) {
            this.setState({ saving: false });
        }
    };

    handleTaxSave = async (event) => {
        event.preventDefault();
        const { taxDraft } = this.state;
        this.setState({ taxSaving: true });
        try {
            const { settings, taxStatus } = await this.props.dispatch(saveStripeTaxSettingsRequest({
                StripeTaxEnabled: !!taxDraft.stripeTaxEnabled,
                OfflineTaxMode: taxDraft.offlineTaxMode,
                ReserveTaxSource: taxDraft.reserveTaxSource,
                DefaultTaxCode: taxDraft.defaultTaxCode,
                FoodTaxCode: taxDraft.foodTaxCode,
                AlcoholTaxCode: taxDraft.alcoholTaxCode,
                MerchandiseTaxCode: taxDraft.merchandiseTaxCode,
                BusinessCountry: taxDraft.businessCountry,
                BusinessState: taxDraft.businessState,
                BusinessCity: taxDraft.businessCity,
                BusinessPostalCode: taxDraft.businessPostalCode,
                BusinessLine1: taxDraft.businessLine1,
            }));
            this.setState({
                taxSettings: settings,
                taxDraft: settings,
                taxStatus,
                taxSaving: false,
            });
        } catch (err) {
            this.setState({ taxSaving: false });
        }
    };

    handleTestCalculate = async () => {
        const amount = Number(this.state.calcAmount);
        if (!(amount > 0)) {
            this.setState({ calcResult: 'Enter a positive amount.' });
            return;
        }
        this.setState({ calcBusy: true, calcResult: '' });
        try {
            const data = await this.props.dispatch(calculateStripeTaxRequest({
                Amount: amount,
                Currency: 'usd',
                RecordTransaction: false,
                Channel: 'preview',
            }));
            const tax = data?.calculation?.taxAmountExclusive
                ?? data?.calculation?.TaxAmountExclusive
                ?? 0;
            this.setState({
                calcBusy: false,
                calcResult: `Tax on $${amount.toFixed(2)} → $${Number(tax).toFixed(2)} (calc ${data?.calculation?.calculationId || data?.calculation?.CalculationId || 'ok'})`,
            });
            await this.loadTaxSettings();
        } catch (err) {
            this.setState({
                calcBusy: false,
                calcResult: err?.message || 'Calculation failed.',
            });
        }
    };

    handleRemittance = async () => {
        const amount = Number(this.state.remitAmount);
        if (!(amount > 0)) {
            this.setState({ remitResult: 'Enter a positive remittance amount.' });
            return;
        }
        this.setState({ remitBusy: true, remitResult: '' });
        try {
            const data = await this.props.dispatch(recordStripeTaxRemittanceRequest({
                Amount: amount,
                Reference: `admin-remit-${Date.now()}`,
            }));
            this.setState({
                remitBusy: false,
                remitAmount: '',
                remitResult: `Recorded $${amount.toFixed(2)}. Ledger net now $${Number(data?.ledgerTaxNet || 0).toFixed(2)}.`,
            });
            await this.loadTaxSettings();
        } catch (err) {
            this.setState({
                remitBusy: false,
                remitResult: err?.message || 'Remittance failed.',
            });
        }
    };

    renderPayrollStates() {
        const { jurisdictionPackages, allowedStates, jurisdictionSaving, jurisdictionError } = this.state;
        const certified = (jurisdictionPackages || []).filter((pkg) => !!(pkg.RebelityCertified || pkg.rebelityCertified));
        if (certified.length === 0 && !jurisdictionError) {
            return null;
        }

        return (
            <div className={s.statusBox}>
                <span className={s.detailLabel}>Payroll states</span>
                <p className={s.helpText}>
                    Check can tax all 50 states. Rebelity still decides which packages this location may run.
                    Georgia is certified. Additional states appear here after Rebelity certifies them.
                </p>
                {jurisdictionError ? <p className={s.errorText}>{jurisdictionError}</p> : null}
                {certified.map((pkg) => {
                    const code = pkg.State || pkg.state;
                    const name = pkg.Name || pkg.name;
                    const enabled = (allowedStates || []).indexOf(code) >= 0;
                    const lastEnabled = enabled && (allowedStates || []).length === 1;
                    return (
                        <FormGroup check className={s.toggleRow} key={code}>
                            <CustomInput
                                type="switch"
                                id={`payrollState-${code}`}
                                label={`${name} (${code})${enabled ? ' · on' : ''}`}
                                checked={enabled}
                                disabled={jurisdictionSaving || lastEnabled}
                                onChange={(e) => this.toggleJurisdiction(code, e.target.checked)}
                            />
                        </FormGroup>
                    );
                })}
            </div>
        );
    }

    render() {
        const {
            draft,
            settings,
            taxDraft,
            taxSettings,
            taxStatus,
            loading,
            saving,
            taxSaving,
            calcBusy,
            calcAmount,
            calcResult,
            remitBusy,
            remitAmount,
            remitResult,
            taxFiling,
            loadError,
            taxLoadError,
        } = this.state;

        const hasChanges =
            draft.payrollEnabled !== settings.payrollEnabled
            || draft.includeTipsAndGratuityInPayroll !== settings.includeTipsAndGratuityInPayroll;

        const taxHasChanges = JSON.stringify(taxDraft) !== JSON.stringify(taxSettings);

        return (
            <div className={s.page}>
                <Widget className={s.payrollWidget}>
                    <div className={s.cards}>
                        <div className={s.sectionCard}>
                            <div className={s.sectionCardHeader}>{this.props.t('payroll.heading')}</div>
                            <div className={s.sectionCardBody}>
                                <p className={s.intro}>{this.props.t('payroll.intro')}</p>
                                {loadError ? <p className={s.errorText}>{loadError}</p> : null}
                                {loading && !loadError ? (
                                    <p className={s.noteText}>{this.props.t('payroll.loading')}</p>
                                ) : null}
                                {!loading && !loadError ? (
                                    <Form onSubmit={this.handleSave}>
                                        <div className={s.toggleGroup}>
                                            <div className={s.toggleItem}>
                                                <FormGroup check className={s.toggleRow}>
                                                    <CustomInput
                                                        type="switch"
                                                        id="payrollEnabled"
                                                        label={this.props.t('payroll.enable')}
                                                        checked={!!draft.payrollEnabled}
                                                        onChange={this.handleToggle('payrollEnabled')}
                                                        disabled={saving}
                                                    />
                                                </FormGroup>
                                            </div>
                                            <div className={s.toggleItem}>
                                                <FormGroup check className={s.toggleRow}>
                                                    <CustomInput
                                                        type="switch"
                                                        id="includeTipsAndGratuityInPayroll"
                                                        label={this.props.t('payroll.includeTips')}
                                                        checked={!!draft.includeTipsAndGratuityInPayroll}
                                                        onChange={this.handleToggle('includeTipsAndGratuityInPayroll')}
                                                        disabled={saving}
                                                    />
                                                </FormGroup>
                                            </div>
                                        </div>
                                        {this.renderCheckStatus()}
                                        {this.renderPayrollStates()}
                                        <div className={s.statusBox}>
                                            <span className={s.detailLabel}>{this.props.t('payroll.engine')}</span>
                                            <span className={s.detailValue}>
                                                {(this.state.engine?.connected || draft.payrollEngineConnected)
                                                    ? this.props.t('common.connected')
                                                    : this.props.t('common.notConnected')}
                                            </span>
                                            <p className={s.helpText}>
                                                {this.state.engine?.message
                                                    || draft.payrollEngineStatusMessage
                                                    || settings.payrollEngineStatusMessage
                                                    || this.props.t('payroll.engineFallback')}
                                            </p>
                                        </div>
                                        <div>
                                            {this.state.engineLoadError ? (
                                                <p className={s.errorText}>{this.state.engineLoadError}</p>
                                            ) : null}
                                            <FormGroup>
                                                <label htmlFor="peBaseUrl">{this.props.t('payroll.baseUrl')}</label>
                                                <Input
                                                    id="peBaseUrl"
                                                    placeholder="http://localhost:5001"
                                                    value={this.state.engineDraft.baseUrl}
                                                    onChange={this.handleEngineField('baseUrl')}
                                                    disabled={this.state.engineSaving}
                                                />
                                            </FormGroup>
                                            <FormGroup>
                                                <label htmlFor="peApiKey">{this.props.t('payroll.apiKey')}</label>
                                                <Input
                                                    id="peApiKey"
                                                    type="password"
                                                    autoComplete="off"
                                                    placeholder={this.state.engine?.apiKeyConfigured ? this.props.t('payroll.keySaved') : this.props.t('common.optional')}
                                                    value={this.state.engineDraft.apiKey}
                                                    onChange={this.handleEngineField('apiKey')}
                                                    disabled={this.state.engineSaving}
                                                />
                                            </FormGroup>
                                            <p className={s.helpText}>
                                                {this.props.t('payroll.engineHelp')}
                                            </p>
                                            <button
                                                type="button"
                                                className={`${s.primaryBtn} ${s.saveBtn}`}
                                                disabled={this.state.engineSaving}
                                                onClick={this.handleEngineSave}
                                            >
                                                {this.state.engineSaving ? this.props.t('payroll.connecting') : this.props.t('payroll.saveTest')}
                                            </button>
                                        </div>
                                        <div className={s.openPayrollRow}>
                                            <a
                                                className={`${s.primaryBtn} ${s.openPayrollBtn}`}
                                                href={`${config.payrollAppUrl}/app`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {this.props.t('payroll.openPayroll')}
                                            </a>
                                        </div>
                                        <button
                                            type="submit"
                                            className={`${s.primaryBtn} ${s.saveBtn}`}
                                            disabled={saving || !hasChanges}
                                        >
                                            {saving ? this.props.t('common.saving') : this.props.t('payroll.save')}
                                        </button>
                                    </Form>
                                ) : null}
                            </div>
                        </div>

                        <div className={s.sectionCard}>
                            <div className={s.sectionCardHeader}>{this.props.t('payroll.taxHeading')}</div>
                            <div className={s.sectionCardBody}>
                                <p className={s.intro}>
                                    {this.props.t('payroll.taxIntro')}
                                </p>
                                {taxLoadError ? <p className={s.errorText}>{taxLoadError}</p> : null}
                                <Form onSubmit={this.handleTaxSave}>
                                    <div className={s.toggleGroup}>
                                        <div className={s.toggleItem}>
                                            <FormGroup check className={s.toggleRow}>
                                                <CustomInput
                                                    type="switch"
                                                    id="stripeTaxEnabled"
                                                    label="Enable Stripe Tax"
                                                    checked={!!taxDraft.stripeTaxEnabled}
                                                    onChange={this.handleTaxToggle('stripeTaxEnabled')}
                                                    disabled={taxSaving}
                                                />
                                            </FormGroup>
                                            <p className={s.helpText}>
                                                When off, Reserve Tax keeps using the legacy Rebelity collected-tax formula only.
                                            </p>
                                        </div>
                                    </div>

                                    <div className={s.fieldGrid}>
                                        <FormGroup>
                                            <label htmlFor="offlineTaxMode">Offline / cash tax mode (T2)</label>
                                            <Input
                                                id="offlineTaxMode"
                                                type="select"
                                                value={taxDraft.offlineTaxMode}
                                                onChange={this.handleTaxField('offlineTaxMode')}
                                                disabled={taxSaving || !taxDraft.stripeTaxEnabled}
                                            >
                                                <option value="TaxApi">Tax API + Tax Transactions (default)</option>
                                                <option value="StripeChannelsOnly">Stripe channels only (no cash Tax Transactions)</option>
                                            </Input>
                                        </FormGroup>

                                        <FormGroup>
                                            <label htmlFor="reserveTaxSource">Reserve Tax source (T3)</label>
                                            <Input
                                                id="reserveTaxSource"
                                                type="select"
                                                value={taxDraft.reserveTaxSource}
                                                onChange={this.handleTaxField('reserveTaxSource')}
                                                disabled={taxSaving || !taxDraft.stripeTaxEnabled}
                                            >
                                                <option value="StripeTaxNet">Stripe Tax ledger net (default)</option>
                                                <option value="RebelityFormula">Rebelity Sale.Tax formula only</option>
                                                <option value="Both">Both (Stripe Tax + Rebelity formula)</option>
                                            </Input>
                                            <p className={s.helpText}>
                                                Used when Payout → Reserve Tax is enabled. “Both” adds the optional separate formula on top of Stripe Tax net.
                                            </p>
                                        </FormGroup>

                                        <FormGroup>
                                            <label htmlFor="defaultTaxCode">Default Stripe tax code</label>
                                            <Input
                                                id="defaultTaxCode"
                                                value={taxDraft.defaultTaxCode}
                                                onChange={this.handleTaxField('defaultTaxCode')}
                                                disabled={taxSaving}
                                            />
                                            <p className={s.helpText}>
                                                Used when a ticket mixes categories or a product has no override. Products can override this on Product Edit.
                                            </p>
                                        </FormGroup>

                                        <FormGroup>
                                            <label htmlFor="foodTaxCode">Food tax code</label>
                                            <Input
                                                id="foodTaxCode"
                                                value={taxDraft.foodTaxCode}
                                                onChange={this.handleTaxField('foodTaxCode')}
                                                disabled={taxSaving}
                                            />
                                        </FormGroup>

                                        <FormGroup>
                                            <label htmlFor="alcoholTaxCode">Alcohol tax code</label>
                                            <Input
                                                id="alcoholTaxCode"
                                                value={taxDraft.alcoholTaxCode}
                                                onChange={this.handleTaxField('alcoholTaxCode')}
                                                disabled={taxSaving}
                                            />
                                        </FormGroup>

                                        <FormGroup>
                                            <label htmlFor="merchandiseTaxCode">Merchandise tax code</label>
                                            <Input
                                                id="merchandiseTaxCode"
                                                value={taxDraft.merchandiseTaxCode}
                                                onChange={this.handleTaxField('merchandiseTaxCode')}
                                                disabled={taxSaving}
                                            />
                                            <p className={s.helpText}>
                                                Atlanta / GA presets: prepared food txcd_40060003, alcohol txcd_40040000, general merchandise txcd_99999999.
                                            </p>
                                        </FormGroup>
                                    </div>

                                    <div className={s.statusBox}>
                                        <span className={s.detailLabel}>Stripe Tax status</span>
                                        <span className={s.detailValue}>
                                            {taxStatus?.activeRegistrationCount > 0
                                                ? `${taxStatus.activeRegistrationCount} registration(s)`
                                                : 'No active registrations'}
                                        </span>
                                        <p className={s.helpText}>{taxStatus?.message || '—'}</p>
                                        <p className={s.helpText}>
                                            Ledger tax net: ${Number(taxStatus?.ledgerTaxNet || 0).toFixed(2)}
                                            {taxStatus?.stripeAccountId ? ` · ${taxStatus.stripeAccountId}` : ''}
                                        </p>
                                    </div>

                                    <div className={s.openPayrollRow}>
                                        <Input
                                            style={{ maxWidth: 140 }}
                                            value={calcAmount}
                                            onChange={(e) => this.setState({ calcAmount: e.target.value })}
                                            disabled={calcBusy || !taxDraft.stripeTaxEnabled}
                                        />
                                        <button
                                            type="button"
                                            className={s.primaryBtn}
                                            disabled={calcBusy || !taxDraft.stripeTaxEnabled}
                                            onClick={this.handleTestCalculate}
                                        >
                                            {calcBusy ? 'Calculating…' : 'Test calculate'}
                                        </button>
                                    </div>
                                    {calcResult ? <p className={s.helpText}>{calcResult}</p> : null}

                                    <p className={s.helpText} style={{ marginTop: 16 }}>
                                        File sales tax in Stripe Tax (merchant-liable). Rebelity does not file a second return.
                                        Record remittances here so Reserve Tax shrinks.
                                    </p>
                                    {taxFiling?.links ? (
                                        <p className={s.helpText}>
                                            <a href={taxFiling.links.taxDashboard || taxFiling.links.TaxDashboard} target="_blank" rel="noreferrer">Stripe Tax dashboard</a>
                                            {' · '}
                                            <a href={taxFiling.links.registrations || taxFiling.links.Registrations} target="_blank" rel="noreferrer">Registrations</a>
                                            {' · '}
                                            <a href={taxFiling.links.docs || taxFiling.links.Docs} target="_blank" rel="noreferrer">Filing docs</a>
                                        </p>
                                    ) : null}
                                    <div className={s.openPayrollRow}>
                                        <Input
                                            style={{ maxWidth: 140 }}
                                            placeholder="Remit $"
                                            value={remitAmount}
                                            onChange={(e) => this.setState({ remitAmount: e.target.value })}
                                            disabled={remitBusy || !taxDraft.stripeTaxEnabled}
                                        />
                                        <button
                                            type="button"
                                            className={s.primaryBtn}
                                            disabled={remitBusy || !taxDraft.stripeTaxEnabled}
                                            onClick={this.handleRemittance}
                                        >
                                            {remitBusy ? 'Recording…' : 'Record remittance'}
                                        </button>
                                    </div>
                                    {remitResult ? <p className={s.helpText}>{remitResult}</p> : null}

                                    <button
                                        type="submit"
                                        className={`${s.primaryBtn} ${s.saveBtn}`}
                                        disabled={taxSaving || !taxHasChanges}
                                    >
                                        {taxSaving ? this.props.t('common.saving') : this.props.t('payroll.saveTax')}
                                    </button>
                                </Form>
                            </div>
                        </div>
                    </div>
                </Widget>
            </div>
        );
    }
}

export default connect()(withTranslation(PayrollSettings));
